/**
 * Roles in a studio draft (decision 4): drafted and built with the
 * workspace, people as names only, invited after Build (board
 * `canvas-inspect-role` 5.12). The rules mirror the team routes'
 * (`apps/server/src/team-rules.ts`): a role holds together, and a record at
 * Change needs the role to add or change entries; admins open everything and
 * are never given access.
 *
 * Row-level reach ("only home deliveries") is not in RecordOS: the agent
 * records it as a not-yet item with the closest fit, never as a role part.
 */
import { enFlat } from '../i18n/en';
import {
  allPermissions,
  hasReach,
  isOn,
  isReach,
  memberPermissions,
  PERMISSION_KEYS,
  unmetNeeds,
  viewerPermissions,
  type PermissionKey,
  type RolePermissions,
} from '../roles';
import { STUDIO_LIMITS, type StudioDraft, type StudioRole } from './draft';
import type { StudioProblem } from './judge';
import type { StudioOp } from './ops';

const NAME_MAX = 60;
const PURPOSE_MAX = 500;

const PRESETS: Readonly<Record<StudioRole['base'], () => RolePermissions>> = {
  admin: allPermissions,
  member: memberPermissions,
  viewer: viewerPermissions,
};

/** The role's permissions: its base preset, then each pair laid over it. A pair naming no permission is skipped. */
export const studioRolePermissions = (role: StudioRole): RolePermissions => {
  const permissions = { ...PRESETS[role.base]() } as Record<string, unknown>;
  for (const { key, value } of role.permissions) {
    if ((PERMISSION_KEYS as readonly string[]).includes(key)) permissions[key] = value;
  }
  return permissions as RolePermissions;
};

/** The names every workspace's roles are made with, which a drafted role never takes. */
const MADE_WITH = (): string[] =>
  (['authority.admin', 'authority.member', 'authority.viewer'] as const).map((key) => (enFlat[key] ?? '').toLowerCase());

/** Every problem with one role, all at once. Path and part are `role:<key>`. */
export const roleProblems = (role: StudioRole, draft: StudioDraft): StudioProblem[] => {
  const path = `role:${role.key}`;
  const problems: StudioProblem[] = [];
  const say = (code: string, message: string): void => {
    problems.push({ code, message, path, part: path });
  };

  const name = role.name.trim();
  if (name.length === 0 || name.length > NAME_MAX) say('ROLE_NAME', `A role is named in 1 to ${NAME_MAX} characters.`);
  const lower = name.toLowerCase();
  if (
    name.length > 0 &&
    (draft.roles.some((other) => other.key !== role.key && other.name.trim().toLowerCase() === lower) || MADE_WITH().includes(lower))
  ) {
    say('ROLE_NAME_TAKEN', `"${name}" is already a role's name. Give this one a name of its own.`);
  }
  if (role.purpose !== undefined && role.purpose.length > PURPOSE_MAX) say('ROLE_PURPOSE', `A role's purpose is ${PURPOSE_MAX} characters at most.`);
  if (role.people.length > STUDIO_LIMITS.rolePeople || role.people.some((person) => person.trim().length === 0 || person.trim().length > NAME_MAX)) {
    say('ROLE_PEOPLE', `A role names ${STUDIO_LIMITS.rolePeople} people at most, each in 1 to ${NAME_MAX} characters.`);
  }

  const seen = new Set<string>();
  for (const { key, value } of role.permissions) {
    if (!(PERMISSION_KEYS as readonly string[]).includes(key)) {
      say('ROLE_PERMISSION', `"${key}" is not a permission.`);
      continue;
    }
    if (seen.has(key)) say('ROLE_PERMISSION', `"${key}" is set twice.`);
    seen.add(key);
    if (hasReach(key as PermissionKey) ? !isReach(value) : typeof value !== 'boolean') {
      say('ROLE_PERMISSION', `"${key}" takes ${hasReach(key as PermissionKey) ? 'off, own, team or everyone' : 'true or false'}.`);
    }
  }

  const permissions = studioRolePermissions(role);
  const unmet = unmetNeeds(permissions);
  if (unmet.length > 0) {
    say('ROLE_NEEDS', `Switch on what these need first: ${unmet.map((one) => `${one.key} (${one.needs.join(', ')})`).join('; ')}.`);
  }

  const granted = new Set<string>();
  for (const row of role.access) {
    if (!draft.records.some((record) => record.key === row.record)) {
      say('ROLE_RECORD_UNKNOWN', `"${row.record}" is not a record or list of this draft.`);
    }
    if (granted.has(row.record)) say('ROLE_ACCESS_TWICE', `"${row.record}" is given twice.`);
    granted.add(row.record);
  }
  if (role.base === 'admin' && role.access.length > 0) {
    say('ROLE_ADMIN_OPENS_ALL', 'An admin opens everything, so it is never given access record by record.');
  }
  const writes = isOn(permissions, 'add_entries') || isOn(permissions, 'change_entries');
  if (!writes && role.access.some((row) => row.level === 'change')) {
    say('ROLE_CHANGE_NEEDS_WRITE', 'A record at Change needs the role to add or change entries.');
  }
  return problems;
};

/* ---------------------------------------------------- what the words said */

/** Words that say a role only looks: view only, read only, only sees, sirf dekhe. */
const LOOK_ONLY = /\b(?:view[- ]?only|read[- ]?only|look[- ]?only|only (?:to )?(?:see|view|look|read)s?|sirf dekh\w*|bas dekh\w*|hanya (?:lihat|melihat))\b/iu;
/** Words that say a role changes nothing: "but not change anything", "cannot add or change anything". */
const CHANGES_NOTHING = /\b(?:not|never|cannot|can'?t|won'?t|no)\s+(?:\w+\s+){0,2}?(?:add|change|edit|modify|touch)\w*(?:\s+or\s+\w+)?\s+(?:anything|nothing|any entries)\b/iu;
/** Words that say a role adds entries, so "only sees" is about what it sees, never that it only looks. */
const ADDS = /\b(?:adds?|enters?|records?|writes?|fills?|makes?)\b/iu;
/** Words that say a look-only role reads the money: everything, the accounts, an accountant. */
const SEES_MONEY = /\b(?:everything|all|accounts?|accountant|money|balances?|auditor|ca|hisaa?b|profit)\b/iu;
/** Words that hold a role to the entries of one place: his own store, only her branch. */
const OWN_PLACE = /\b(?:own|only (?:his|her|their))\s+(?:own\s+)?(store|shop|branch|outlet|site|location|area|route|counter|kitchen|godown|warehouse|dukaan|kedai|cabang|toko)\b/iu;

/** Whether a role's words say it only looks: view only, or it changes nothing, and never that it adds. */
export const saysLookOnly = (words: string): boolean => CHANGES_NOTHING.test(words) || (LOOK_ONLY.test(words) && !ADDS.test(words));

/**
 * The ops that give a drafted role what its words said, when the role still
 * holds only the Member preset it was drawn with (stores, round 2: "Mr Joshi
 * should see everything but not change anything" was built able to add
 * entries and blind to the money; "each manager sees only his own store"
 * saw everyone's entries).
 *
 *   look only        a role whose words say it only looks is a viewer, and
 *                    sees the money when they say everything, the accounts
 *                    or an accountant
 *   one place        a role held to one store's or branch's entries is not
 *                    in RecordOS (row-level reach): its closest fit is the
 *                    entries each person adds (Own), and a not-yet item says
 *                    so plainly, once
 *
 * A role the model gave a base or permissions of its own is left. Empty when
 * no role's words say more than its preset.
 */
export const roleWordsOps = (draft: Pick<StudioDraft, 'roles' | 'notYet'>): StudioOp[] => {
  const ops: StudioOp[] = [];
  for (const role of draft.roles) {
    if (role.base !== 'member' || role.permissions.length > 0) continue;
    const words = role.purpose ?? '';
    if (saysLookOnly(words)) {
      ops.push({ op: 'update_role', role: role.key, base: 'viewer', permissions: SEES_MONEY.test(words) ? [{ key: 'see_money', value: true }] : [] });
      continue;
    }
    const place = OWN_PLACE.exec(words)?.[1]?.toLowerCase();
    if (place === undefined) continue;
    ops.push({
      op: 'update_role',
      role: role.key,
      permissions: [
        { key: 'see_entries', value: 'own' },
        { key: 'download_entries', value: 'own' },
      ],
    });
    const need = `A role that sees only the entries of one ${place}`;
    if (draft.notYet.some((item) => item.need === need)) continue;
    ops.push({
      op: 'not_yet',
      item: {
        kind: 'role',
        name: `${role.name}: only their own ${place}`.slice(0, 80),
        fit: `A role cannot be held to one ${place}'s entries yet. For now ${role.name} sees and changes only the entries each person adds.`,
        level: 'arrangement',
        archetype: 'other',
        need,
      },
    });
  }
  return ops;
};

